import * as React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON, postJSON } from '@/lib/api'
import type { EmailTemplate, Fields } from '@/pages/SystemEmailTemplatesPage'

interface PreviewSample {
  contact_id: number
  name: string
}

interface PreviewResponse {
  preview: {
    sample_contact_id: number
    sample_name: string
    subject: string
    rendered_body: string
  }
}

interface TestSendResponse {
  email_send: { status: string; skip_reason: string | null }
}

const EMPTY_SAMPLES: PreviewSample[] = []

export function TemplatePreviewCard({
  fields,
  onNotice,
  selected,
}: {
  fields: Fields
  onNotice: (notice: string) => void
  selected: EmailTemplate
}) {
  const [sampleContactId, setSampleContactId] = React.useState('')
  const samplesQuery = useQuery({
    queryKey: ['system-email-template-samples', selected.kind],
    queryFn: () =>
      getJSON<{ samples: PreviewSample[] }>(
        `/api/v1/system_email_templates/${selected.kind}/samples`,
      ),
    retry: false,
  })
  const samples = samplesQuery.data?.samples ?? EMPTY_SAMPLES
  React.useEffect(() => {
    setSampleContactId((current) => selectedSampleId(samples, current))
  }, [samples])
  const previewQuery = useTemplatePreview(selected, sampleContactId, useDebouncedValue(fields, 250))
  const testSend = useTestSend(selected, sampleContactId, fields, onNotice)

  return (
    <Card data-testid="email-template-preview">
      <CardHeader className="border-b border-border p-4">
        <CardTitle className="text-body font-semibold">Preview</CardTitle>
        {samples.length > 0 && (
          <SampleSelect samples={samples} value={sampleContactId} onChange={setSampleContactId} />
        )}
      </CardHeader>
      <CardContent className="space-y-3 p-4">
        {samplesQuery.isLoading && <Status>Loading sample members...</Status>}
        {samplesQuery.isError && <Alert>Sample members unavailable</Alert>}
        {!samplesQuery.isLoading && !samplesQuery.isError && samples.length === 0 && (
          <Alert>No sample member data</Alert>
        )}
        {previewQuery.isLoading && <Status>Rendering preview...</Status>}
        {previewQuery.isFetching && previewQuery.data && <Status>Updating preview...</Status>}
        {previewQuery.isError && <Alert>Email preview unavailable</Alert>}
        {previewQuery.data && (
          <RenderedPreview
            pending={testSend.isPending}
            preview={previewQuery.data.preview}
            sendTest={() => {
              onNotice('')
              testSend.mutate()
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}

function useTemplatePreview(selected: EmailTemplate, sampleContactId: string, fields: Fields) {
  return useQuery({
    queryKey: ['system-email-template-preview', selected.kind, sampleContactId, fields],
    queryFn: () =>
      postJSON<PreviewResponse>(`/api/v1/system_email_templates/${selected.kind}/preview`, {
        contact_id: Number(sampleContactId),
        system_email_template: fields,
      }),
    enabled: Boolean(sampleContactId),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === selected.kind ? previous : undefined,
    retry: false,
  })
}

function useTestSend(
  selected: EmailTemplate,
  sampleContactId: string,
  fields: Fields,
  onNotice: (notice: string) => void,
) {
  return useMutation({
    mutationFn: () =>
      postJSON<TestSendResponse>(`/api/v1/system_email_templates/${selected.kind}/test_send`, {
        contact_id: Number(sampleContactId),
        system_email_template: fields,
      }),
    onSuccess: ({ email_send }) => onNotice(testSendNotice(email_send)),
    onError: (error) => onNotice(`Test send failed: ${error.message}`),
  })
}

function SampleSelect({
  onChange,
  samples,
  value,
}: {
  onChange: (value: string) => void
  samples: PreviewSample[]
  value: string
}) {
  return (
    <FormField label="Sample member" htmlFor="email-preview-sample">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id="email-preview-sample">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {samples.map((sample) => (
            <SelectItem key={sample.contact_id} value={sample.contact_id.toString()}>
              {sample.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}

function RenderedPreview({
  pending,
  preview,
  sendTest,
}: {
  pending: boolean
  preview: PreviewResponse['preview']
  sendTest: () => void
}) {
  return (
    <>
      <div>
        <p className="text-caption text-muted-foreground">Sample data · {preview.sample_name}</p>
        <p className="text-body-sm font-semibold">{preview.subject}</p>
      </div>
      <iframe
        className="h-[420px] w-full rounded-md border border-border bg-background"
        sandbox=""
        srcDoc={preview.rendered_body}
        title="Email preview"
      />
      <Button type="button" variant="outline" disabled={pending} onClick={sendTest}>
        Send test to me
      </Button>
    </>
  )
}

function Status({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-caption text-muted-foreground" role="status">
      {children}
    </p>
  )
}

function selectedSampleId(samples: PreviewSample[], current: string) {
  return samples.some((sample) => sample.contact_id.toString() === current)
    ? current
    : (samples[0]?.contact_id.toString() ?? '')
}

function testSendNotice(emailSend: TestSendResponse['email_send']) {
  return emailSend.status === 'sent'
    ? 'Test sent to your email address.'
    : `Test not sent: ${emailSend.skip_reason ?? emailSend.status}.`
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timeout)
  }, [delay, value])
  return debounced
}
